import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/600.css'
import '@fontsource/roboto/700.css'
import '@fontsource/roboto-mono/600.css'
import '@fontsource/roboto-mono/700.css'
import type { CSSProperties } from 'react'
import type { ThemeOptions } from '@mui/material'

/**
 * Overrides MUI's TS-module declaration for the sake of adding
 * 2 custom variants: code1 and code2.
 */
declare module '@mui/material/styles' {
    interface TypographyVariants {
        code1: CSSProperties
        code2: CSSProperties
    }

    // allows configuration using `createTheme()`
    interface TypographyVariantsOptions {
        code1?: CSSProperties
        code2?: CSSProperties
    }
}

// updates the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        code1: true
        code2: true
    }
}

/**
 * Figma's own component specs reference this same type scale by a different set of names
 * (`Display 2xl`, `Text sm`, `Code xs`, ...) than the MUI variants below. Each variant's comment
 * gives that name, so a spec written in those terms (e.g. "Text sm") maps back to the variant to
 * use here (`body2`).
 */
export const typography: ThemeOptions['typography'] = {
    /** Figma: Display 2xl */
    h1: {
        fontSize: '4.5rem', // 72px
        lineHeight: '5.625rem', // 90px
        letterSpacing: '-0.02em',
    },
    /** Figma: Display xl */
    h2: {
        fontSize: '3.75rem', // 60px
        lineHeight: '4.5rem', // 72px
        letterSpacing: '-0.02em',
    },
    /** Figma: Display lg */
    h3: {
        fontSize: '3rem', // 48px
        lineHeight: '3.75rem', // 60px
        letterSpacing: '-0.02em',
    },
    /** Figma: Display md */
    h4: {
        fontSize: '2.25rem', // 36px
        lineHeight: '2.75rem', // 44px
        letterSpacing: '-0.02em',
    },
    /** Figma: Display sm */
    h5: {
        fontSize: '1.875rem', // 30px
        lineHeight: '2.375rem', // 38px
        letterSpacing: 0,
    },
    /** Figma: Display xs */
    h6: {
        fontSize: '1.5rem', // 24px
        lineHeight: '2rem', // 32px
        letterSpacing: 0,
    },
    /** Figma: Text xl */
    subtitle1: {
        fontSize: '1.25rem', // 20px
        lineHeight: '1.875rem', // 30px
        letterSpacing: 0,
    },
    /** Figma: Text lg */
    subtitle2: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 0,
    },
    /** Figma: Text md */
    body1: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 0,
    },
    /** Figma: Text sm */
    body2: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 0,
    },
    /** Figma: Text xs */
    caption: {
        fontSize: '0.75rem', // 12px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 0,
    },
    /** Figma: Code sm */
    code1: {
        fontFamily: "'Roboto Mono', Consolas, Menlo, monospace",
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
    },
    /** Figma: Code xs */
    code2: {
        fontFamily: "'Roboto Mono', Consolas, Menlo, monospace",
        fontSize: '0.75rem', // 12px
        lineHeight: '1.125rem', // 18px
    },
}
